import { isFunction, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import { MediaInfoRawData } from "../../model/MediaInfo.js";
import { AudioInfoRawData } from "../../model/audio/AudioInfo.js";
import { VideoInfoRawData } from "../../model/video/VideoInfo.js";
import { MediaControlOrigin } from "../events/PlayerEvent.js";
import { GenericFormatLabel, ParsedFormat } from "./video/FormatAndSizePreference.js";

/**
 * Represents the set of supported media event names for the player module.
 * 
 * These event names correspond to common HTMLMediaElement events and can be used
 * to type-safe event handling throughout the media player codebase.
 *
 * Supported event names include:
 * - `timeupdate`: Fired when the current playback position changes.
 * - `progress`: Fired periodically to indicate buffering progress.
 * - `pause`: Fired when playback is paused.
 * - `play`: Fired when playback starts or resumes.
 * - `complete`: Custom event indicating media playback is complete.
 * - `waiting`: Fired when playback is delayed pending further data.
 * - `stalled`: Fired when media data is unexpectedly not available.
 * - `ended`: Fired when playback reaches the end of the media.
 * - `loadedmetadata`: Fired when metadata has been loaded.
 * - `volumechange`: Fired when the volume changes.
 * - `playing`: Fired when playback is ready to start after buffering.
 * - `suspend`: Fired when media data loading is suspended.
 * - `seeking`: Fired when a seek operation starts.
 */
export type MediaEventName = keyof {
    timeupdate: boolean,
    progress: boolean,
    pause: boolean,
    play: boolean,
    complete: boolean,
    waiting: boolean,
    stalled: boolean,
    ended: boolean,
    loadedmetadata: boolean,
    volumechange: boolean,
    playing: boolean,
    suspend: boolean,
    seeking: boolean
};


/**
 * Represents statistics related to the loading of a media resource.
 *
 * @property loadStartTime - The timestamp (in milliseconds) when the media resource loading started.
 * @property loadEndTime - The timestamp (in milliseconds) when the media resource loading finished.
 */
export type MediaResourceStats = {
    loadStartTime: number,
    loadEndTime: number,
}

/**
 * Represents a reference to a media element along with its associated metadata and execution context.
 *
 * @template EL - The type of the HTML media element (e.g., HTMLVideoElement, HTMLAudioElement).
 * @template MI - The type of the media information raw data.
 *
 * @property element - The HTML media element instance.
 * @property info - The raw media information associated with the element.
 * @property executionId - The identifier for the execution context.
 * @method ensureResourceLoad - Ensures that the media resource is loaded and ready for use.
 * @returns A promise that resolves when the resource has been loaded.
 */
export interface MediaRef<EL extends HTMLMediaElement, MI extends MediaInfoRawData<any>> {
    element: EL
    info: MI
    executionId: ExecutionId
    ensureResourceLoad(): Promise<void>
}


/**
 * Represents a reference to a video media resource.
 * Extends {@link MediaRef} with an HTMLVideoElement and raw video information.
 *
 * @remarks
 * This interface is typically used to provide type safety and metadata for video elements
 * within the media player module.
 */
export interface VideoRef extends MediaRef<HTMLVideoElement, VideoInfoRawData> {

}

/**
 * Represents a reference to an audio media resource.
 * Extends {@link MediaRef} with an {@link HTMLAudioElement} and {@link AudioInfoRawData}.
 *
 * @remarks
 * This interface is used to provide type safety and metadata for audio elements within the media player module.
 */
export interface AudioRef extends MediaRef<HTMLAudioElement, AudioInfoRawData> {

}

/**
 * Represents a unique identifier for a media resource execution.
 * Typically used to track or reference a specific execution instance.
 */
export type ExecutionId = string

let executionCounter = 0;

/**
 * Generates a unique execution ID for a given media resource.
 *
 * The execution ID is composed of an incremented execution counter,
 * the provided media ID, and two random hexadecimal segments.
 *
 * @param mediaId - The identifier of the media resource.
 * @returns A unique execution ID string in the format: `{counter}-{mediaId}-{random1}-{random2}`.
 */
function getExecutionId(mediaId: string): ExecutionId {
 
    const S4 = () => Math.floor(Math.random() * 0x10000 /* 65536 */).toString(16);

    const uExec = ++executionCounter;
    return `${uExec}-${mediaId}-${S4()}-${S4()}`;
}



/**
 * Abstract base class representing a media resource within a media player context.
 * 
 * @typeParam MediaPlayer - The type of the media player instance associated with this resource.
 * @typeParam EL - The type of the HTML media element (e.g., HTMLVideoElement, HTMLAudioElement).
 * @typeParam MI - The type of the raw media info data.
 * 
 * @implements MediaRef<EL, MI>
 * 
 * @remarks
 * This class provides a common interface and partial implementation for managing media resources,
 * including playback control, metadata access, event handling, and resource lifecycle operations.
 * Subclasses must implement abstract methods to provide specific behavior for different media types.
 * 
 * @param player - The media player instance associated with this resource.
 * @param element - The HTML media element used for playback.
 * @param listItem - The DOM element representing the resource in a list or UI.
 * @param info - The raw media information data.
 * @param fixedDuration - An optional fixed duration for the media resource.
 * @param stats - Statistics and metrics related to the media resource.
 * 
 * @property duration - Gets the duration of the media resource, using `fixedDuration` if set, otherwise the internal duration.
 * @property executionId - Unique identifier for the current execution context of the resource.
 * @property metadataPromise - Resolves when the media element's metadata is loaded.
 * 
 * @method play - Begins playback of the media resource, recording the origin of the control.
 * @method pause - Pauses playback, recording the origin of the control.
 * @method restart - Restarts the media resource and updates the execution ID.
 * @method reset - Resets the media resource and updates the execution ID.
 * @method seek - Seeks to a specific time in the media resource.
 * @method reloadSource - Reloads the media source, optionally keeping the current time.
 * @method show - Makes the media resource visible in the UI.
 * @method hide - Hides the media resource from the UI.
 * @method muted - Gets or sets the muted state of the media resource.
 * @method volume - Gets or sets the volume of the media resource.
 * @method autoplay - Gets or sets the autoplay state of the media resource.
 * @method loop - Gets or sets the loop state of the media resource.
 * @method beforeUnload - Performs cleanup before the resource is unloaded.
 * @method on - Registers an event listener for a media event.
 * @method off - Removes an event listener for a media event.
 * @method once - Registers a one-time event listener for a media event.
 * @method destroy - Cleans up and releases all resources associated with this media resource.
 * @method ensureResourceLoad - Ensures the resource is loaded and ready for playback.
 * @method fetchLastMediaControlOrigin - Retrieves and resets the last recorded media control origin.
 * 
 * @abstract
 * Subclasses must implement playback control, state accessors, and resource lifecycle methods.
 */
export abstract class MediaResource<MediaPlayer, EL extends HTMLMediaElement, MI extends MediaInfoRawData<any>> implements MediaRef<EL, MI> {
    private executionIdInt: ExecutionId;
    private lastOrigin: MediaControlOrigin;

    constructor(
        public readonly player: MediaPlayer,
        public readonly element: EL,
        public readonly listItem: HTMLElement,
        public readonly info: MI,
        protected readonly fixedDuration: number,
        public readonly stats: MediaResourceStats
    ) {
        this.executionIdInt = getExecutionId(info.id);
    }


    protected abstract get internalDuration(): number;
    protected abstract internalPlay(): Promise<any>;
    protected abstract internalRestartOperations(): Promise<any>;
    protected abstract internalResetOperations(): Promise<any>;
    protected abstract internalPause(): Promise<any>;

    abstract get ended(): boolean;
    abstract get currentTime(): number;
    abstract get isPaused(): boolean;

    abstract destroy(): void;
    abstract seek(time: number): Promise<any>;
    abstract reloadSource(keepCurrentTime: boolean): Promise<void>

    abstract show(): void;
    abstract hide(): void;
    abstract muted(muted?: boolean): boolean;
    abstract volume(volume?: number): number;
    abstract autoplay(autoplay?: boolean): boolean;
    abstract loop(loopEnabled?: boolean): boolean;

    abstract beforeUnload(): Promise<void>;
    abstract on(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void;
    abstract off(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void;

    get duration(): number {
        if (this.fixedDuration > 0) return this.fixedDuration;
        else return this.internalDuration;
    }

    get executionId(): ExecutionId {
        return this.executionIdInt;
    }

    ensureResourceLoad(): Promise<void> {
        return Promise.resolve();
    }

    play(origin: MediaControlOrigin): Promise<any> {
        this.lastOrigin = origin;
        return this.internalPlay();
    }

    restart(): Promise<any> {
        this.executionIdInt = getExecutionId(this.info.id);
        return this.internalRestartOperations();
    }

    reset(): Promise<any> {
        this.executionIdInt = getExecutionId(this.info.id);
        return this.internalResetOperations();
    }

    fetchLastMediaControlOrigin(): MediaControlOrigin {
        const fetched = this.lastOrigin;
        this.lastOrigin = null;
        return fetched ? fetched : MediaControlOrigin.NON_USER;
    }

    pause(origin: MediaControlOrigin): Promise<any> {
        this.lastOrigin = origin;
        return this.internalPause();
    }

    once(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void {
        const nuListener = (evt: Event) => {
            if (isObject(listener) && ("handleEvent" in listener) && isFunction(listener.handleEvent)) {
                listener.handleEvent(evt);
            }
            else if (isFunction(listener)) {
                listener(evt);
            }
            this.off(eventName, nuListener);

        }
        this.on(eventName, nuListener);
    }

    get metadataPromise(): Promise<void> {
        return this.element.readyState >= 1 ? Promise.resolve() :
            new Promise((resolve) => {
                this.once("loadedmetadata", () => resolve());
            });
    }
}




/**
 * Abstract class representing a video media resource within a media player.
 * 
 * @template MediaPlayer - The type of the media player instance.
 * 
 * @extends MediaResource
 * 
 * @param player - The media player instance associated with this resource.
 * @param element - The HTML video element used for playback.
 * @param listItem - The HTML element representing the resource in a list.
 * @param info - Raw data containing information about the video.
 * @param fixedDuration - The fixed duration of the video, if applicable.
 * @param stats - Statistics related to the media resource.
 * @param availQual - Array of available video quality formats.
 * 
 * @remarks
 * This class provides methods for toggling fullscreen mode, retrieving available video qualities,
 * and abstract methods for getting the current format and switching quality.
 */
export abstract class VideoResource<MediaPlayer> extends MediaResource<MediaPlayer, HTMLVideoElement, VideoInfoRawData> {
    constructor(
        public readonly player: MediaPlayer,
        public readonly element: HTMLVideoElement,
        public readonly listItem: HTMLElement,
        public readonly info: VideoInfoRawData,
        protected readonly fixedDuration: number,
        public readonly stats: MediaResourceStats,
        private readonly availQual: ParsedFormat[]
    ) {
        super(player, element, listItem, info, fixedDuration, stats)
    }

    toggleFullscreen(fullscreen: boolean): boolean {
        return fullscreen
    }

    getAvailableQualities(): ParsedFormat[] {
        return this.availQual;
    }

    abstract getCurrentFormat(): ParsedFormat;

    abstract switchQuality(formatLabel: GenericFormatLabel): Promise<ParsedFormat>;
}
/**
 * Represents an abstract audio resource for a media player.
 * 
 * @typeParam MediaPlayer - The type of the media player associated with this resource.
 * 
 * @extends MediaResource
 * 
 * @param player - The media player instance associated with this audio resource.
 * @param element - The HTML audio element used for playback.
 * @param listItem - The HTML element representing the audio resource in a list or UI.
 * @param info - The raw data containing information about the audio resource.
 * @param fixedDuration - The fixed duration of the audio resource, in seconds.
 * @param stats - The statistics object for tracking resource usage and playback metrics.
 * 
 * @remarks
 * This class should be extended to implement specific audio resource behaviors.
 * 
 * @method getCurrentSource - Returns the current source URL of the audio resource.
 */
export abstract class AudioResource<MediaPlayer> extends MediaResource<MediaPlayer, HTMLAudioElement, AudioInfoRawData> {
    constructor(
        public readonly player: MediaPlayer,
        public readonly element: HTMLAudioElement,
        public readonly listItem: HTMLElement,
        public readonly info: AudioInfoRawData,
        protected readonly fixedDuration: number,
        public readonly stats: MediaResourceStats,
    ) {
        super(player, element, listItem, info, fixedDuration, stats)
    }

    abstract getCurrentSource(): string;

}